/**
 * Insights Path Converter
 *
 * Bidirectional conversion between tree node keys and insight paths.
 * Supports 3 granularity levels:
 * - @insight[analysisId]                       — whole analysis
 * - @insight[analysisId].category              — whole JTBD category
 * - @insight[analysisId].category[insightId]   — single insight item
 */

import type { InsightsTreeNode } from './types';
import { findNodeByKey } from '../../shared/treeHelpers';

/**
 * Converts a tree node key to a path string.
 * Only item-level nodes produce paths directly.
 * Category and analysis nodes are handled by path optimization.
 */
export function treeNodeIdToPath(nodeKey: string, allNodes: InsightsTreeNode[]): string | null {
  const node = findNodeByKey(nodeKey, allNodes) as InsightsTreeNode | null;
  if (!node) return null;

  if (node.nodeType === 'item' && node.insightId && node.category) {
    return `@insight[${node.analysisId}].${node.category}[${node.insightId}]`;
  }

  // Category and analysis nodes don't produce paths — they expand to children
  return null;
}

/**
 * Converts checked tree node IDs to optimized paths.
 *
 * Optimization rules:
 * - If all items in a category are checked → emit @insight[id].category (category-level path)
 * - If all categories in an analysis are checked → emit @insight[id] (analysis-level path)
 * - Otherwise → emit individual item paths
 */
export function treeNodeIdsToPaths(checkedIds: string[], allNodes: InsightsTreeNode[]): string[] {
  const checkedSet = new Set(checkedIds);
  const paths: string[] = [];

  for (const analysisNode of allNodes) {
    if (analysisNode.nodeType !== 'analysis') continue;
    const categoryNodes = (analysisNode.children || []) as InsightsTreeNode[];

    // Check if all categories are fully checked
    let allCategoriesFullyChecked = categoryNodes.length > 0;

    for (const catNode of categoryNodes) {
      if (catNode.nodeType !== 'category') continue;
      const itemNodes = (catNode.children || []) as InsightsTreeNode[];

      // Check if all items in this category are checked
      const allItemsChecked = itemNodes.length > 0 && itemNodes.every(
        (item) => checkedSet.has(item.key as string),
      );

      if (allItemsChecked && catNode.category) {
        // Whole category selected — will be either category-level or analysis-level path
        if (!allCategoriesFullyChecked) {
          // Not all categories fully checked — emit category-level path
          paths.push(`@insight[${catNode.analysisId}].${catNode.category}`);
        }
      } else {
        allCategoriesFullyChecked = false;
        // Individual items — emit item-level paths
        for (const item of itemNodes) {
          if (checkedSet.has(item.key as string)) {
            const path = treeNodeIdToPath(item.key as string, allNodes);
            if (path) paths.push(path);
          }
        }
      }
    }

    if (allCategoriesFullyChecked && categoryNodes.length > 0) {
      // All categories fully checked — emit analysis-level path
      paths.push(`@insight[${analysisNode.analysisId}]`);
    }
  }

  return paths;
}

/**
 * Converts paths to tree node IDs (for hydration/restoring selection state).
 *
 * Handles 3 levels:
 * - @insight[id] → expands to all item nodes in analysis
 * - @insight[id].category → expands to all item nodes in category
 * - @insight[id].category[itemId] → maps to single item node
 */
export function pathsToTreeNodeIds(paths: string[], allNodes: InsightsTreeNode[]): string[] {
  const keys = new Set<string>();

  for (const path of paths) {
    // Try analysis-level: @insight[id]
    const analysisMatch = path.match(/^@insight\[([^\]]+)\]$/);
    if (analysisMatch) {
      const analysisId = analysisMatch[1];
      // Expand to all item nodes in this analysis
      for (const analysisNode of allNodes) {
        if (analysisNode.analysisId !== analysisId) continue;
        for (const catNode of (analysisNode.children || []) as InsightsTreeNode[]) {
          for (const item of (catNode.children || []) as InsightsTreeNode[]) {
            keys.add(item.key as string);
          }
        }
      }
      continue;
    }

    // Try category-level: @insight[id].category
    const categoryMatch = path.match(/^@insight\[([^\]]+)\]\.([^[.]+)$/);
    if (categoryMatch) {
      const [, analysisId, category] = categoryMatch;
      // Expand to all item nodes in this category
      for (const analysisNode of allNodes) {
        if (analysisNode.analysisId !== analysisId) continue;
        for (const catNode of (analysisNode.children || []) as InsightsTreeNode[]) {
          if (catNode.category !== category) continue;
          for (const item of (catNode.children || []) as InsightsTreeNode[]) {
            keys.add(item.key as string);
          }
        }
      }
      continue;
    }

    // Try item-level: @insight[id].category[itemId]
    // The category class excludes `.` (W13 DM1, AC-W13-33): a loose `[^[]+`
    // would read `@insight[a].fears.sources[item-9]` as category `fears.sources`
    // and promote the quote's item key to a phantom thesis id.
    const itemMatch = path.match(/^@insight\[([^\]]+)\]\.([^[\].]+)\[([^\]]+)\]$/);
    if (itemMatch) {
      const [, analysisId, category, insightId] = itemMatch;
      const nodeKey = `insight-${analysisId}-${category}-${insightId}`;
      const node = findNodeByKey(nodeKey, allNodes);
      if (node) {
        keys.add(nodeKey);
      }
    }
  }

  return Array.from(keys);
}

/**
 * Smart path deduplication for insights.
 * Removes duplicates and ensures no overlap between levels.
 */
export function deduplicatePaths(paths: string[]): string[] {
  return [...new Set(paths)];
}
